import React from "react";
import { Navigate, Outlet, Link, NavLink, useNavigate } from "react-router-dom";
import { Inbox, Settings2, MapPin, Home as HomeIcon, Calendar, LogOut, ExternalLink, ShoppingBag, BookOpen } from "lucide-react";
import { useAdminAuth } from "../../lib/adminAuth";

export default function AdminLayout() {
  const { token, bootChecked, user, logout } = useAdminAuth();
  const navigate = useNavigate();

  if (!bootChecked) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-cream-100">
        <div className="text-ink/55 text-sm">Yükleniyor…</div>
      </div>
    );
  }
  if (!token) return <Navigate to="/admin/login" replace />;

  const linkCls = ({ isActive }) =>
    `flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm font-light transition-colors ${
      isActive ? "bg-ink text-cream-100" : "text-ink/70 hover:bg-cream-200/60 hover:text-ink"
    }`;

  return (
    <div data-testid="admin-layout" className="min-h-screen bg-cream-100 text-ink">
      <div className="grid lg:grid-cols-[260px_1fr] min-h-screen">
        <aside className="border-r border-cream-300/60 bg-cream-50 p-5 lg:p-6 flex flex-col">
          <Link to="/" className="flex items-baseline gap-1 mb-1" data-testid="admin-back-to-site">
            <span className="font-serif text-2xl">in</span>
            <span className="font-serif italic text-2xl text-terracotta-400">Touch</span>
          </Link>
          <span className="text-[10px] uppercase tracking-[0.24em] text-ink/45 mb-8">Admin</span>

          <nav className="flex flex-col gap-1.5">
            <NavLink to="/admin" end className={linkCls} data-testid="admin-nav-messages">
              <Inbox className="w-4 h-4" strokeWidth={1.5} />
              Mesajlar
            </NavLink>
            <NavLink to="/admin/home" className={linkCls} data-testid="admin-nav-home">
              <HomeIcon className="w-4 h-4" strokeWidth={1.5} />
              Anasayfa
            </NavLink>
            <NavLink to="/admin/services" className={linkCls} data-testid="admin-nav-services">
              <Settings2 className="w-4 h-4" strokeWidth={1.5} />
              Hizmetler
            </NavLink>
            <NavLink to="/admin/events" className={linkCls} data-testid="admin-nav-events">
              <Calendar className="w-4 h-4" strokeWidth={1.5} />
              Etkinlikler
            </NavLink>
            <NavLink to="/admin/orders" className={linkCls} data-testid="admin-nav-orders">
              <ShoppingBag className="w-4 h-4" strokeWidth={1.5} />
              Siparişler
            </NavLink>
            <NavLink to="/admin/courses" className={linkCls} data-testid="admin-nav-courses">
              <BookOpen className="w-4 h-4" strokeWidth={1.5} />
              Eğitimler
            </NavLink>
            <NavLink to="/admin/contact-info" className={linkCls} data-testid="admin-nav-contact">
              <MapPin className="w-4 h-4" strokeWidth={1.5} />
              İletişim Bilgileri
            </NavLink>
          </nav>

          <div className="mt-auto pt-6 border-t border-cream-300/60 space-y-3">
            <a
              href="/"
              target="_blank"
              rel="noreferrer"
              className="flex items-center gap-2 text-xs text-ink/55 hover:text-ink"
              data-testid="admin-view-site"
            >
              <ExternalLink className="w-3.5 h-3.5" strokeWidth={1.5} />
              Siteyi görüntüle
            </a>
            <div className="text-xs text-ink/55">
              <p className="font-medium text-ink">{user?.username}</p>
            </div>
            <button
              data-testid="admin-logout"
              onClick={() => {
                logout();
                navigate("/admin/login");
              }}
              className="w-full flex items-center justify-center gap-2 rounded-full border border-ink/15 px-4 py-2 text-xs hover:bg-ink hover:text-cream-100 transition-colors"
            >
              <LogOut className="w-3.5 h-3.5" strokeWidth={1.5} />
              Çıkış Yap
            </button>
          </div>
        </aside>

        <main className="p-6 md:p-10 max-w-5xl">
          <Outlet />
        </main>
      </div>

      <style>{`
        .admin-input, .admin-textarea {
          width: 100%;
          background: #FBFAF6;
          border: 1px solid #E5E0D8;
          border-radius: 0.75rem;
          padding: 0.65rem 0.9rem;
          font-family: 'Outfit', sans-serif;
          font-size: 0.92rem;
          color: #2C3531;
          font-weight: 300;
          transition: all 0.2s ease;
        }
        .admin-textarea { resize: vertical; min-height: 80px; line-height: 1.55; }
        .admin-input:focus, .admin-textarea:focus {
          outline: none;
          border-color: #839788;
          background: #FFFFFF;
          box-shadow: 0 0 0 3px rgba(131, 151, 136, 0.12);
        }
        .admin-label {
          display: block;
          font-size: 11px;
          text-transform: uppercase;
          letter-spacing: 0.18em;
          color: rgba(44,53,49,0.55);
          margin-bottom: 6px;
          font-weight: 500;
        }
      `}</style>
    </div>
  );
}
